// src/hooks/useOptimizedSubscription.js
import { useState, useEffect, useCallback, useRef } from 'react';
import useAuthToken from './useAuthToken';
import optimizedApiClient from '../utils/optimizedApiClient';
import { notifySubscriptionError } from './useSubscriptionNotifications';
// Import production logger with timeout protection
import productionLogger from '../utils/productionLogger';

/**
 * Optimized subscription hook with intelligent caching, deduplication, and performance monitoring
 */
const useOptimizedSubscription = () => {
  const [subscription, setSubscription] = useState({
    loading: true,
    data: null,
    error: null,
    retryCount: 0,
    lastFetch: null,
    isFresh: false
  });

  const token = useAuthToken();
  const abortControllerRef = useRef(null);
  const lastFetchTimeRef = useRef(0);
  const MINIMUM_FETCH_INTERVAL = 1000; // Prevent fetching more than once per second

  // Memoized fetch function to prevent unnecessary re-creations
  const fetchSubscription = useCallback(async (force = false) => {
    // Skip subscription checks on admin routes
    const isAdminRoute = window.location.pathname.startsWith('/admin');
    if (isAdminRoute) {
      console.log('🔒 useOptimizedSubscription: Admin route detected, skipping subscription check');
      setSubscription(prev => ({ 
        ...prev, 
        loading: false, 
        data: null, 
        error: null 
      }));
      return;
    }

    if (!token) {
      console.log('🔒 useOptimizedSubscription: No auth token, skipping API call');
      setSubscription(prev => ({ 
        ...prev, 
        loading: false, 
        data: null, 
        error: null 
      }));
      return;
    }

    // Prevent too frequent fetches
    const now = Date.now();
    if (!force && (now - lastFetchTimeRef.current) < MINIMUM_FETCH_INTERVAL) {
      console.log('🚫 Fetch throttled - too frequent requests');
      return;
    }
    lastFetchTimeRef.current = now;

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    const startTime = performance.now();
    
    try {
      setSubscription(prev => ({ 
        ...prev, 
        loading: true, 
        error: null 
      }));
      
      const response = await optimizedApiClient.get('/billing/subscription', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: abortControllerRef.current.signal
      });

      const endTime = performance.now();
      const fetchDuration = endTime - startTime;
      
      // Log API call performance
      productionLogger.logApiCall('/billing/subscription', 'GET', fetchDuration, true, {
        cached: fetchDuration < 100,
        sessionId: productionLogger.getSessionId()
      });

      const data = response.data;
      
      setSubscription({
        loading: false,
        data,
        error: null,
        retryCount: 0,
        lastFetch: new Date().toISOString(),
        isFresh: fetchDuration < 100 // Consider fresh if fetched quickly (likely from cache)
      });

      // Log performance metrics in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`📊 Subscription fetch: ${fetchDuration.toFixed(1)}ms ${data ? '(cached)' : '(fresh)'}`);
      }

    } catch (error) {
      // Don't set error state if request was aborted
      if (error.name === 'AbortError') {
        console.log('🚫 Subscription fetch aborted');
        return;
      }

      const fetchDuration = performance.now() - startTime;
      
      // Log API call failure
      productionLogger.logApiCall('/billing/subscription', 'GET', fetchDuration, false, {
        error: error.message,
        status: error.response?.status,
        sessionId: productionLogger.getSessionId()
      });

      console.warn('Failed to fetch subscription:', error);
      
      // Notify subscription notification system of the error
      notifySubscriptionError(error);
      
      // Enhanced error message based on error type
      let errorMessage = 'Failed to load subscription information';
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        switch (status) {
          case 401:
            errorMessage = 'Your session has expired. Please refresh the page and log in again.';
            break;
          case 403:
            errorMessage = 'Access denied. Your account may not have the required permissions.';
            break;
          case 404:
            errorMessage = 'Subscription information not found. This may be a new account.';
            break;
          case 429:
            errorMessage = 'Too many requests. Please wait a moment and try again.';
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            errorMessage = 'Our servers are temporarily unavailable. Please try again in a few minutes.';
            break;
          default:
            errorMessage = data?.detail || error.message || 'An unexpected error occurred';
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to our servers. Please check your internet connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }
      
      setSubscription(prev => ({
        loading: false,
        data: prev.data, // Keep existing data if available
        error: errorMessage,
        retryCount: prev.retryCount + 1,
        lastFetch: new Date().toISOString(),
        isFresh: false
      }));
    }
  }, [token]);

  // Initial fetch effect with delayed execution to prevent cancellation
  useEffect(() => {
    // Only fetch if we have a token
    if (token) {
      // Add small delay to ensure component is stable and avoid immediate cancellation
      const timer = setTimeout(() => {
        fetchSubscription();
      }, 100); // 100ms delay to ensure stable mount
      
      return () => clearTimeout(timer);
    }
    
    // Cleanup function to abort any pending request
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchSubscription, token]);

  // Optimized cancel subscription with cache invalidation
  const cancelSubscription = useCallback(async (reason, feedback = '', cancelImmediately = false) => {
    if (!token) {
      throw new Error('Your session has expired. Please refresh the page and log in again.');
    }

    // Validate inputs
    if (!reason || reason.trim() === '') {
      throw new Error('Please select a cancellation reason.');
    }

    // Check network connectivity
    if (!navigator.onLine) {
      throw new Error('No internet connection. Please check your connection and try again.');
    }

    try {
      const startTime = performance.now();
      
      const response = await optimizedApiClient.post('/billing/cancel-subscription', {
        reason,
        feedback,
        cancel_immediately: cancelImmediately
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = response.data;
      
      // Log successful subscription operation
      productionLogger.logSubscriptionOperation('cancellation', {
        duration: performance.now() - startTime,
        cancelImmediately,
        sessionId: productionLogger.getSessionId()
      });
    
      // Update local state optimistically
      setSubscription(prev => ({
        ...prev,
        data: {
          ...prev.data,
          ...result.subscription
        },
        isFresh: true
      }));

      return result;
    } catch (error) {
      // Log subscription operation failure
      productionLogger.logSubscriptionOperation('cancellation', {
        error: error.message,
        status: error.response?.status,
        sessionId: productionLogger.getSessionId()
      });
      
      // Notify subscription notification system of the error
      notifySubscriptionError(error);
      
      // Enhanced error handling with specific messages
      let errorMessage = 'Failed to cancel subscription';
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        switch (status) {
          case 400:
            if (data?.detail?.includes('already canceled')) {
              errorMessage = 'Your subscription is already canceled.';
            } else {
              errorMessage = data?.detail || 'Invalid cancellation request';
            }
            break;
          case 401:
            errorMessage = 'Your session has expired. Please refresh the page and log in again.';
            break;
          case 403:
            errorMessage = 'You don\'t have permission to cancel this subscription.';
            break;
          case 404:
            errorMessage = 'Subscription not found. It may have already been canceled.';
            break;
          case 409:
            errorMessage = 'A billing change is already in progress. Please wait and try again.';
            break;
          case 429:
            errorMessage = 'Too many requests. Please wait a few minutes and try again.';
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            errorMessage = 'Service temporarily unavailable. Please try again in a few minutes.';
            break;
          default:
            errorMessage = data?.detail || 'An unexpected error occurred during cancellation';
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to our servers. Please check your internet connection and try again.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }
      
      throw new Error(errorMessage);
    }
  }, [token]);

  // Optimized reactivate subscription with cache invalidation
  const reactivateSubscription = useCallback(async () => {
    if (!token) {
      throw new Error('Your session has expired. Please refresh the page and log in again.');
    }

    // Check network connectivity
    if (!navigator.onLine) {
      throw new Error('No internet connection. Please check your connection and try again.');
    }

    try {
      const response = await optimizedApiClient.post('/billing/reactivate-subscription', {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = response.data;
      
      // Update local state optimistically
      setSubscription(prev => ({
        ...prev,
        data: {
          ...prev.data,
          ...result.subscription
        },
        isFresh: true
      }));

      return result;
    } catch (error) {
      // Notify subscription notification system of the error
      notifySubscriptionError(error);
      
      // Enhanced error handling with specific messages
      let errorMessage = 'Failed to reactivate subscription';
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        switch (status) {
          case 400:
            if (data?.detail?.includes('payment method')) {
              errorMessage = 'Please add a valid payment method before reactivating. Visit billing settings to add payment details.';
            } else if (data?.detail?.includes('cannot be reactivated')) {
              errorMessage = 'This subscription cannot be reactivated. Please contact support.';
            } else {
              errorMessage = data?.detail || 'Invalid reactivation request';
            }
            break;
          case 401:
            errorMessage = 'Your session has expired. Please refresh the page and log in again.';
            break;
          case 403:
            errorMessage = 'You don\'t have permission to reactivate this subscription.';
            break;
          case 404:
            errorMessage = 'Subscription not found or already active.';
            break;
          case 409:
            errorMessage = 'Your subscription is already active or a billing change is in progress.';
            break;
          case 429:
            errorMessage = 'Too many requests. Please wait a few minutes and try again.';
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            errorMessage = 'Service temporarily unavailable. Please try again in a few minutes.';
            break;
          default:
            errorMessage = data?.detail || 'An unexpected error occurred during reactivation';
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to our servers. Please check your internet connection and try again.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }
      
      throw new Error(errorMessage);
    }
  }, [token]);

  // Manual refresh function
  const refreshSubscription = useCallback((force = false) => {
    fetchSubscription(force);
  }, [fetchSubscription]);

  // Automatic retry logic for temporary failures
  useEffect(() => {
    if (subscription.error && subscription.retryCount < 3) {
      const shouldAutoRetry = (
        subscription.error.includes('temporarily unavailable') ||
        subscription.error.includes('network') ||
        subscription.error.includes('timeout') ||
        subscription.error.includes('connect')
      );
      
      if (shouldAutoRetry) {
        const retryDelay = Math.min(1000 * Math.pow(2, subscription.retryCount), 10000); // Exponential backoff, max 10s
        console.log(`Auto-retrying subscription fetch in ${retryDelay}ms (attempt ${subscription.retryCount + 1}/3)`);
        
        const retryTimer = setTimeout(() => {
          fetchSubscription(true);
        }, retryDelay);
        
        return () => clearTimeout(retryTimer);
      }
    }
  }, [subscription.error, subscription.retryCount, fetchSubscription]);

  const subscriptionAPI = {
    subscription: subscription.data,
    loading: subscription.loading,
    error: subscription.error,
    retryCount: subscription.retryCount,
    lastFetch: subscription.lastFetch,
    isFresh: subscription.isFresh,
    refreshSubscription,
    cancelSubscription,
    reactivateSubscription,
    // Helper functions
    isActive: subscription.data?.status === 'active' && !subscription.data?.cancel_at_period_end,
    isCanceled: subscription.data?.status === 'canceled' || subscription.data?.cancel_at_period_end,
    isTrialing: subscription.data?.status === 'trialing',
    canCancel: ['active', 'trialing'].includes(subscription.data?.status) && !subscription.data?.cancel_at_period_end,
    canReactivate: (subscription.data?.status === 'canceled' && subscription.data?.can_reactivate) || subscription.data?.cancel_at_period_end
  };
  
  return subscriptionAPI;
};

export default useOptimizedSubscription;